/**
 * Creamy Loots — Lookbook Page JS
 * Custom cursor · clip-path reveal · parallax · drag strip · look counter
 */
(function () {
  'use strict';

  const isTouch   = window.matchMedia('(hover: none)').matches;
  const reducedMo = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── CUSTOM CURSOR ──────────────────────────────────────────────────────── */
  /* El cursor global (cl-cursor.liquid / #cl-cur) gestiona el cursor en todo
     el sitio incluyendo el lookbook. Esta función sólo corre como fallback si
     el cursor global no está presente en la página. */

  function initCursor() {
    if (isTouch) return;
    /* Si el cursor global ya está activo, no crear un segundo cursor */
    if (document.getElementById('cl-cur')) return;

    const cursor = document.getElementById('cl-lp-cursor');
    if (!cursor) return;

    const label = cursor.querySelector('.cl-lp__cursor-label');
    let   mx = -200, my = -200, cx = -200, cy = -200;
    let   raf;

    document.addEventListener('mousemove', e => {
      mx = e.clientX;
      my = e.clientY;
    });

    const lerp = (a, b, t) => a + (b - a) * t;

    const tick = () => {
      cx = lerp(cx, mx, 0.14);
      cy = lerp(cy, my, 0.14);
      cursor.style.left = cx + 'px';
      cursor.style.top  = cy + 'px';
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    /* image areas → expand + label */
    document.querySelectorAll('.cl-lp__scene-image-wrap, .cl-lp__bts-item').forEach(el => {
      el.addEventListener('mouseenter', () => {
        cursor.classList.add('is-image');
        if (label) label.textContent = 'VIEW';
      });
      el.addEventListener('mouseleave', () => cursor.classList.remove('is-image'));
    });

    /* buttons → small variant */
    document.querySelectorAll('.cl-btn, .cl-btn-ghost').forEach(el => {
      el.addEventListener('mouseenter', () => {
        cursor.classList.add('is-btn');
        if (label) label.textContent = '→';
      });
      el.addEventListener('mouseleave', () => cursor.classList.remove('is-btn'));
    });
  }

  /* ── SCROLL REVEAL ──────────────────────────────────────────────────────── */

  function initReveal() {
    const selectors = ['[data-lp-reveal]', '[data-lp-reveal-ed]', '[data-lp-reveal-up]'];
    const all = document.querySelectorAll(selectors.join(','));
    if (!all.length) return;

    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -48px 0px' }
    );

    all.forEach(el => obs.observe(el));
  }

  /* ── PARALLAX ───────────────────────────────────────────────────────────── */

  function initParallax() {
    if (reducedMo) return;
    const imgs = document.querySelectorAll('[data-lp-parallax]');
    if (!imgs.length) return;

    const update = () => {
      imgs.forEach(img => {
        const wrap = img.closest('[data-lp-parallax-wrap]');
        if (!wrap) return;
        const rect  = wrap.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const rel   = (window.innerHeight * 0.5 - rect.top - rect.height * 0.5) / window.innerHeight;
        const speed = parseFloat(img.dataset.lpParallax) || 0.1;
        img.style.transform = `translateY(${(rel * speed * 100).toFixed(1)}px)`;
      });
    };

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── FLOATING LOOK COUNTER ──────────────────────────────────────────────── */

  function initCounter() {
    const counter  = document.getElementById('cl-lp-counter');
    const scenes   = document.querySelectorAll('[data-lp-scene]');
    if (!counter || !scenes.length) return;

    const current  = counter.querySelector('.cl-lp__counter-current');
    const total    = counter.querySelector('.cl-lp__counter-total');
    const barFill  = counter.querySelector('.cl-lp__counter-bar-fill');

    total.textContent = String(scenes.length).padStart(2, '0');

    let activeIdx = 0;

    const pad = n => String(n).padStart(2, '0');

    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            const idx = [...scenes].indexOf(e.target);
            if (idx !== activeIdx) {
              activeIdx = idx;
              current.textContent = pad(idx + 1);
              if (barFill) barFill.style.transform = `scaleY(${(idx + 1) / scenes.length})`;
            }
          }
        });

        /* Show/hide counter based on scenes visibility */
        const anyVisible = entries.some(e => e.isIntersecting);
        if (anyVisible) counter.classList.add('is-visible');
      },
      { threshold: 0.4 }
    );

    scenes.forEach(el => obs.observe(el));

    /* Hide counter when no scenes visible */
    const hideObs = new IntersectionObserver(
      entries => {
        const allHidden = [...scenes].every(
          el => !el.getBoundingClientRect().height || el.getBoundingClientRect().bottom < 0
        );
        if (allHidden) counter.classList.remove('is-visible');
      },
      { threshold: 0 }
    );
    hideObs.observe(scenes[scenes.length - 1]);
  }

  /* ── DRAG STRIP ─────────────────────────────────────────────────────────── */

  function initDrag() {
    const tracks = document.querySelectorAll('[data-lp-drag]');

    tracks.forEach(track => {
      const inner = track.querySelector('.cl-lp__bts-inner');
      if (!inner) return;

      let isDragging = false;
      let startX = 0;
      let scrollX = 0;
      let velX = 0;
      let lastX = 0;
      let momentum;

      const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
      const maxScroll = () => inner.scrollWidth - track.clientWidth + 56;

      const setX = x => {
        scrollX = clamp(x, 0, maxScroll());
        inner.style.transform = `translateX(${-scrollX}px)`;
      };

      /* Mouse */
      track.addEventListener('mousedown', e => {
        isDragging = true;
        startX  = e.pageX;
        lastX   = e.pageX;
        velX    = 0;
        track.classList.add('is-dragging');
        cancelAnimationFrame(momentum);
        e.preventDefault();
      });

      document.addEventListener('mousemove', e => {
        if (!isDragging) return;
        velX = e.pageX - lastX;
        lastX = e.pageX;
        setX(scrollX - (e.pageX - startX));
        startX = e.pageX;
      });

      document.addEventListener('mouseup', () => {
        if (!isDragging) return;
        isDragging = false;
        track.classList.remove('is-dragging');
        applyMomentum();
      });

      /* Touch */
      track.addEventListener('touchstart', e => {
        startX = e.touches[0].pageX;
        lastX  = startX;
        velX   = 0;
        cancelAnimationFrame(momentum);
      }, { passive: true });

      track.addEventListener('touchmove', e => {
        const dx = e.touches[0].pageX - lastX;
        velX = dx;
        lastX = e.touches[0].pageX;
        setX(scrollX - (e.touches[0].pageX - startX));
        startX = e.touches[0].pageX;
      }, { passive: true });

      track.addEventListener('touchend', applyMomentum);

      function applyMomentum() {
        const decay = 0.93;
        const step = () => {
          if (Math.abs(velX) < 0.5) return;
          setX(scrollX - velX);
          velX *= decay;
          momentum = requestAnimationFrame(step);
        };
        momentum = requestAnimationFrame(step);
      }
    });
  }

  /* ── HERO IMAGE LOAD ZOOM-OUT ───────────────────────────────────────────── */

  function initHeroZoom() {
    const hero = document.querySelector('.cl-lp__hero');
    const img  = hero?.querySelector('.cl-lp__hero-img');
    if (!hero || !img) return;

    if (img.complete) {
      hero.classList.add('is-loaded');
    } else {
      img.addEventListener('load', () => hero.classList.add('is-loaded'), { once: true });
    }
  }

  /* ── INIT ───────────────────────────────────────────────────────────────── */

  function init() {
    initCursor();
    initReveal();
    initParallax();
    initCounter();
    initDrag();
    initHeroZoom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
